export const ALLOWED_INTERVAL_MINUTES = [5, 10, 15, 20, 30, 60, 120, 180, 240, 360, 480, 720, 1440];

export const IANA_TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Toronto',
  'America/Vancouver',
  'America/Halifax',
  'America/St_Johns',
  'America/Mexico_City',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Amsterdam',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Stockholm',
  'Europe/Helsinki',
  'Europe/Athens',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Asia/Shanghai',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Perth',
  'Pacific/Auckland',
];

export const WEEKDAYS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

export function emptyScheduleForm() {
  return {
    mode: 'one_shot',
    timezone: 'America/New_York',
    runAtLocal: '',
    type: 'daily',
    time: '09:00',
    daysOfWeek: [1, 2, 3, 4, 5],
    dayOfMonth: 1,
    intervalMinutes: 15,
    enabled: true,
  };
}

export function formFromTrigger(trigger) {
  const base = emptyScheduleForm();
  if (!trigger) return base;
  const recurring = String(trigger.scheduleMode || '').toLowerCase() === 'recurring';
  const schedule = trigger.schedule || {};
  let runAtLocal = '';
  if (trigger.runAt) {
    const dt = new Date(trigger.runAt);
    if (!Number.isNaN(dt.getTime())) {
      const pad = (n) => String(n).padStart(2, '0');
      runAtLocal = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
    }
  }
  return {
    ...base,
    mode: recurring ? 'recurring' : 'one_shot',
    timezone: schedule.timezone || trigger.timezone || base.timezone,
    runAtLocal,
    type: schedule.type || 'daily',
    time: schedule.time || '09:00',
    daysOfWeek: Array.isArray(schedule.daysOfWeek) && schedule.daysOfWeek.length ? schedule.daysOfWeek : [1, 2, 3, 4, 5],
    dayOfMonth: schedule.dayOfMonth || 1,
    intervalMinutes: ALLOWED_INTERVAL_MINUTES.includes(Number(schedule.intervalMinutes))
      ? Number(schedule.intervalMinutes)
      : 15,
    enabled: trigger.enabled !== false,
  };
}

export function triggerPayloadFromForm(form, { flowId, tenantId } = {}) {
  const enabled = form.enabled !== false;
  if (form.mode === 'one_shot') {
    if (!form.runAtLocal) {
      throw new Error('Run at date/time is required');
    }
    const local = new Date(form.runAtLocal);
    if (Number.isNaN(local.getTime())) throw new Error('Invalid date/time');
    return {
      type: 'scheduled',
      flowId,
      tenantId,
      enabled,
      runAt: local.toISOString().replace(/\.\d{3}Z$/, 'Z'),
    };
  }
  const timezone = String(form.timezone || '').trim();
  if (!timezone) throw new Error('IANA timezone is required');
  const schedule = { type: form.type, timezone };
  if (form.type === 'interval') {
    const minutes = Number(form.intervalMinutes);
    if (!ALLOWED_INTERVAL_MINUTES.includes(minutes)) {
      throw new Error('Interval must be one of the supported values');
    }
    schedule.intervalMinutes = minutes;
  } else {
    schedule.time = String(form.time || '').trim();
  }
  if (form.type === 'weekly') {
    schedule.daysOfWeek = [...(form.daysOfWeek || [])].map(Number).sort((a, b) => a - b);
  }
  if (form.type === 'monthly') {
    schedule.dayOfMonth = Number(form.dayOfMonth);
  }
  return {
    type: 'scheduled',
    flowId,
    tenantId,
    enabled,
    schedule,
  };
}

export function formatClockTime(time) {
  const raw = String(time || '').trim();
  const match = raw.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return raw;
  let hours = Number(match[1]);
  const minutes = match[2];
  if (!Number.isFinite(hours) || hours < 0 || hours > 23) return raw;
  const suffix = hours >= 12 ? 'PM' : 'AM';
  hours %= 12;
  if (hours === 0) hours = 12;
  return `${hours}:${minutes} ${suffix}`;
}

function sortedDayNumbers(days) {
  return [...(Array.isArray(days) ? days : [])]
    .map(Number)
    .filter((day) => Number.isFinite(day))
    .sort((a, b) => a - b);
}

function formatWeeklyDays(days) {
  const values = sortedDayNumbers(days);
  if (!values.length) return 'Weekly';
  if (values.length === 5 && values.join() === '1,2,3,4,5') return 'Weekdays';
  if (values.length === 2 && values[0] === 0 && values[1] === 6) return 'Weekends';
  if (values.length === 7) return 'Daily';
  return values
    .map((day) => WEEKDAYS.find((row) => row.value === day)?.label || day)
    .join(', ');
}

function formatIntervalLabel(minutes) {
  const value = Number(minutes);
  if (!Number.isFinite(value) || value <= 0) return 'Interval';
  if (value % 60 === 0) {
    const hours = value / 60;
    return hours === 1 ? 'Every hour' : `Every ${hours} hours`;
  }
  return `Every ${value} minutes`;
}

function isRecurringTrigger(trigger) {
  if (!trigger) return false;
  if (String(trigger.scheduleMode || '').toLowerCase() === 'recurring') return true;
  return Boolean(trigger.schedule && typeof trigger.schedule === 'object');
}

export function formatCompactScheduleSummary(trigger) {
  if (!trigger) {
    return { text: 'No scheduled trigger', timezone: null, showTimezone: false, disabled: false };
  }
  const disabled = trigger.enabled === false;
  const schedule = trigger.schedule && typeof trigger.schedule === 'object' ? trigger.schedule : {};
  const timezone = String(schedule.timezone || trigger.timezone || '').trim() || null;
  let text = '';
  let showTimezone = false;

  if (isRecurringTrigger(trigger) || schedule.type) {
    const type = String(schedule.type || '').toLowerCase();
    if (type === 'daily') {
      text = `Daily · ${formatClockTime(schedule.time)}`;
      showTimezone = Boolean(timezone);
    } else if (type === 'weekly') {
      text = `${formatWeeklyDays(schedule.daysOfWeek)} · ${formatClockTime(schedule.time)}`;
      showTimezone = Boolean(timezone);
    } else if (type === 'monthly') {
      const day = schedule.dayOfMonth != null ? `Day ${schedule.dayOfMonth}` : 'Monthly';
      text = `Monthly · ${day} · ${formatClockTime(schedule.time)}`;
      showTimezone = Boolean(timezone);
    } else if (type === 'interval') {
      text = formatIntervalLabel(schedule.intervalMinutes);
      showTimezone = false;
    } else if (timezone) {
      text = 'Recurring';
      showTimezone = true;
    } else {
      text = 'Recurring';
    }
  } else if (trigger.runAt) {
    const dt = new Date(trigger.runAt);
    text = Number.isNaN(dt.getTime()) ? 'One-shot' : `One-shot · ${dt.toLocaleString()}`;
  } else {
    text = disabled ? 'Disabled' : 'Scheduled';
  }

  if (disabled && text && text !== 'Disabled') {
    text = `Disabled · ${text}`;
  }

  return { text, timezone, showTimezone, disabled };
}

export function formatTriggerSummary(trigger) {
  const compact = formatCompactScheduleSummary(trigger);
  if (compact.showTimezone && compact.timezone) {
    return `${compact.text} · ${compact.timezone}`;
  }
  return compact.text;
}

export function selectTriggersForFlow(rows, flowId) {
  const id = String(flowId || '').trim();
  if (!id) return [];
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    const rowFlowId = String(row?.flowId || row?.flow_id || '').trim();
    return rowFlowId === id;
  });
}

export function formatFlowScheduleDisplay(triggers = []) {
  const list = Array.isArray(triggers) ? triggers.filter(Boolean) : [];
  if (!list.length) {
    return { text: 'No scheduled trigger', timezone: null, showTimezone: false };
  }
  const recurring = list.filter(isRecurringTrigger);
  const relevant = recurring.length ? recurring : list;
  const first = formatCompactScheduleSummary(relevant[0]);
  if (relevant.length === 1) return first;
  return {
    ...first,
    text: `${first.text} · +${relevant.length - 1}`,
  };
}

export function parseApiErrorPayload(error) {
  const text = String(error?.message || error || '');
  const jsonStart = text.indexOf('{');
  if (jsonStart < 0) return { error: text };
  try {
    return JSON.parse(text.slice(jsonStart));
  } catch {
    return { error: text };
  }
}
